import {Distribution, DistributionType, DropdownItemType,} from "../../model/EstateFlowchartTypes";
import React, {useEffect, useState} from "react";
import {BENEFICIARIES_AT_FIRST_DEATH_CONSTANTS as Labels} from "../../constants/LabelConstants";
import FormPage from "../../components/FormPage/FormPage";
import DistributionList from "../../components/FormContent/DistributionList/DistributionList";
import {getDistributionItemTypeName} from "../../utils/DefUtils";

type BeneficiariesFirstDeathContentProps = {
    distributions: Distribution[];
    updateEstateItem: (distributions: Distribution[]) => void;
    distributionDropdownOptionTypes: DistributionType[];
    saveForm: () => Promise<boolean>;
    pageIndex: number;
    firstDeathMemberName?: string;
};

/**
 * Component for displaying and managing the beneficiaries at the first death in an estate flowchart.
 *
 * @component
 * @param {BeneficiariesFirstDeathContentProps} props - The properties for the component.
 * @param {Function} props.updateEstateItem - Function to update the estate item with new beneficiary information.
 * @param {boolean} props.pageIndex - Index of the page this belongs to.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <BeneficiariesFirstDeathContent
 *   updatedRevocableTrust={updatedRevocableTrustFunction}
 *   pageIndex={1}
 * />
 *
 * @remarks
 * This component uses the `useParams` hook to extract route parameters and the `useAppSelector` hook to access the estate flowcharts from the state.
 * It initializes the beneficiary information based on the revocable trust data and updates the state accordingly.
 * The component conditionally renders either a `MaritalTrustForm` or an `AddDistributionButtonDropdown` based on the presence of beneficiary information.
 *
 */
const BeneficiariesFirstDeathContent: React.FC<BeneficiariesFirstDeathContentProps> =
    ({
         distributions,
         updateEstateItem,
         distributionDropdownOptionTypes,
         saveForm,
         pageIndex,
         firstDeathMemberName,
     }: BeneficiariesFirstDeathContentProps): JSX.Element => {

        const [distributionList, setDistributionList] = useState<Distribution[]>(distributions);

        useEffect(() => {
            setDistributionList(distributions);
        }, [distributions]);

        const dropdownOptions: DropdownItemType[] = distributionDropdownOptionTypes.map((distributionType) => {
            const dropdownItemType : DropdownItemType = {
                name: getDistributionItemTypeName(distributionType),
                type: distributionType
            }
            return dropdownItemType;
        })

        const title = firstDeathMemberName ? `Beneficiaries at ${firstDeathMemberName}'s Death` : Labels.TITLE_LABEL_TEXT;
        return (
            <FormPage title={title} subText={Labels.TITLE_SUB_TEXT} pageIndex={pageIndex}>
                <DistributionList
                    distributions={distributionList}
                    updateEstateItem={(newDistributions) => {
                        setDistributionList(newDistributions);
                        updateEstateItem(newDistributions);
                    }}
                    saveForm={saveForm}
                    pageIndex={pageIndex}
                    addDistributionOptions={dropdownOptions}/>
            </FormPage>
        );
    };

export default BeneficiariesFirstDeathContent;